import {
  CreateConfigParams,
  DeleteConfigParams,
  FetchConfigJsonParams,
  HomeConfigUsecaseInterface,
  LocalizeImageParams,
  UpdateConfigParams,
  UploadImageParams,
} from "./usecase.d";
import {
  GetHomeConfigDto,
  HomeConfigScopeType,
} from "../../services/HomeConfig/dto/get.dto";
import { HomeConfigRepository } from "../../services/HomeConfig/homeConfig.repository";
import { AuthUsecase } from "../Auth/usecase";
import { S3 } from "../../modules/S3";
import { Environment } from "../../config/amplify-configure";
import { ConfigOption, ConfigTreeNode, ConfigurationType } from "./types";
import { HomeConfigUtil } from "./utils";

export class HomeConfigUsecase implements HomeConfigUsecaseInterface {
  repository: HomeConfigRepository;

  constructor() {
    this.repository = new HomeConfigRepository();
  }

  // Create flow for a base home configuration:
  //   1. ask the backend for a presigned S3 PUT URL,
  //   2. upload the configuration JSON straight to S3,
  //   3. record the resulting s3:// link against the home.
  async createConfig(params: CreateConfigParams): Promise<void> {
    const {
      homeId,
      homeName,
      tenantId,
      configuration,
      region,
      community,
      userId,
    } = params;

    const { presignedUrl, key } = await new AuthUsecase().getPresignedUrl({
      tenantId,
      fileStorage: "HomeConfig",
      fileType: "application/json",
    });

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: JSON.stringify(configuration),
    });
    if (!uploadResponse.ok) {
      throw new Error("Failed to upload home configuration to S3");
    }

    const configLink = S3.toS3Uri(presignedUrl, key);
    await this.repository.create(homeId, {
      homeName,
      configLink,
      region,
      community,
      userId,
    });
  }

  // Create flow for a base home configuration:
  //   1. ask the backend for a presigned S3 PUT URL,
  //   2. upload the configuration JSON straight to S3,
  //   3. record the resulting s3:// link against the home.
  async updateConfig(params: UpdateConfigParams): Promise<void> {
    const { homeId, tenantId, configuration, region, community, userId } =
      params;

    const { presignedUrl, key } = await new AuthUsecase().getPresignedUrl({
      tenantId,
      fileStorage: "HomeConfig",
      fileType: "application/json",
    });

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: JSON.stringify(configuration),
    });
    if (!uploadResponse.ok) {
      throw new Error("Failed to upload home configuration to S3");
    }

    const configLink = S3.toS3Uri(presignedUrl, key);
    await this.repository.update(homeId, {
      configLink,
      region,
      community,
      userId,
    });
  }

  async fetchConfigJson(
    params: FetchConfigJsonParams,
  ): Promise<ConfigOption[]> {
    return await S3.fetchS3JsonFile<ConfigOption[]>(params.configLink);
  }

  async uploadImage(params: UploadImageParams): Promise<string> {
    const { tenantId, file } = params;

    const { presignedUrl } = await new AuthUsecase().getPresignedUrl({
      tenantId,
      fileStorage: "HomeConfig",
      fileType: file.type,
    });

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
    });
    if (!uploadResponse.ok) {
      throw new Error("Failed to upload the image to S3");
    }

    // NOTE: The presigned url's query string is the signature; the object's
    // public url is what remains once it is stripped.
    return presignedUrl.split("?")[0];
  }

  async localizeImage(params: LocalizeImageParams): Promise<string> {
    const { tenantId, url } = params;

    const assetEnv = HomeConfigUtil.assetEnv(url);
    if (!assetEnv) {
      throw new Error(`Unsupported image link: ${url}`);
    }
    if (assetEnv === Environment) {
      return url;
    }

    const response = await fetch(url, { cache: "no-store" }).catch(
      () => undefined,
    );
    if (!response?.ok) {
      throw new Error(`Failed to fetch image: ${url}`);
    }
    const blob = await response.blob();
    const file = new File([blob], "image", { type: blob.type });
    return this.uploadImage({ tenantId, file });
  }

  async deleteConfig(params: DeleteConfigParams): Promise<void> {
    const { homeId, region, community, userId } = params;
    await this.repository.delete(homeId, { region, community, userId });
  }

  async getConfigTree(): Promise<ConfigTreeNode[]> {
    const rawConfigs = await this.repository.getList();
    const configs: GetHomeConfigDto[] = rawConfigs.map((item) => {
      return {
        ...item,
        // API has no home name; fall back to the home id for display.
        homeName: item.homeName || item.homeId,
      };
    });

    const baseConfigs = configs.filter((config) => {
      return config.scopeType === "base";
    });

    return baseConfigs.map((base) => {
      return HomeConfigUsecase.toNode(configs, base, 0);
    });
  }

  private static toNode(
    configs: GetHomeConfigDto[],
    config: GetHomeConfigDto,
    depth: number,
  ): ConfigTreeNode {
    const children = this.childrenOf(config, configs).map((child) => {
      return this.toNode(configs, child, depth + 1);
    });

    return {
      key: this.scopeKey(config),
      config,
      type: this.toConfigurationType(config.scopeType),
      displayName: this.displayName(config),
      depth,
      // Leave `children` undefined for leaves so antd shows no expander.
      children: children.length > 0 ? children : undefined,
    };
  }

  // Direct children of a config by its scope level (user-* scopes are leaves).
  private static childrenOf(
    config: GetHomeConfigDto,
    data: GetHomeConfigDto[],
  ): GetHomeConfigDto[] {
    switch (config.scopeType) {
      case "base":
        return data.filter((c) => {
          return (
            c.homeId === config.homeId &&
            (c.scopeType === "region" || c.scopeType === "user-base")
          );
        });
      case "region":
        return data.filter((c) => {
          return (
            c.homeId === config.homeId &&
            c.region === config.region &&
            (c.scopeType === "community" || c.scopeType === "user-region")
          );
        });
      case "community":
        return data.filter((c) => {
          return (
            c.homeId === config.homeId &&
            c.region === config.region &&
            c.community === config.community &&
            c.scopeType === "user-community"
          );
        });
      default:
        return [];
    }
  }

  // Stable React row key derived from the scope tuple.
  private static scopeKey(config: GetHomeConfigDto): string {
    return [
      config.homeId,
      config.scopeType,
      config.region ?? "-",
      config.community ?? "-",
      config.userId ?? "-",
    ].join("#");
  }

  // Scope name shown in the Name column (region / community / user id); base
  // has none — it renders its home name instead.
  private static displayName(config: GetHomeConfigDto): string | undefined {
    switch (config.scopeType) {
      case "region":
        return config.region ?? undefined;
      case "community":
        return config.community ?? undefined;
      case "base":
        return undefined;
      default:
        // user-base | user-region | user-community
        return config.userId ?? undefined;
    }
  }

  // Collapse the three user-* scopes to the single FE "user" type.
  private static toConfigurationType(
    scope: HomeConfigScopeType,
  ): ConfigurationType {
    return scope.startsWith("user-") ? "user" : (scope as ConfigurationType);
  }
}
